import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseUrl, getDateWithDay, requirementStatusList, qtyToCrates, cratesToQty, roundQty } from "../../comman/config";
import { DairyRequirementFilter } from "../../components/Filter/DairyRequirementFilter.js";
import toast, { Toaster } from "react-hot-toast";
import IngredientBatchModel from "../../components/IngredientBatchModel.js";
import OrderIngredientModel from "../../components/DairyOrderModal.js";
import NoRecordsFound from "../../components/NoRecordsFound.js";
import Loader from "../../components/Loader.js";

// Which rows are ticked, held as ingredient + date rather than as the row objects themselves.
// Editing a cell rebuilds the row object, and a selection that pointed at the old one would both
// clear the tick on screen and send the pre-edit quantities when the order is placed.
const rowKey = (item) => `${item.ingredient_id}_${item.date}`;

function DairyOrderList() {
    const today = new Date().toISOString().split("T")[0];
    const [ingredientOrderList, setIngredientOrderList] = useState([]);
    const [orderStatus, setOrderStatus] = useState({});
    const [orderId, setOrderId] = useState({});
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectAll, setSelectAll] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [ingredient, setIngredient] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderIngedientList, setOrderIngredientList] = useState(null);
    const [itemIngedientList, setItemIngredientList] = useState(null);
    const [methodList, setMethodList] = useState([]);
    const [loading, setLoading] = useState(false)
    const [noRecordsFound, setNoRecordsFound] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null)

    useEffect(() => {
        getMethodList()
    }, []);

    useEffect(() => {
        getDairyIngredientOrderList();
    }, [])

    const handelFilterChange = async (from_date, to_date, order_status, order_id, ingredient) => {
        setFromDate(from_date);
        setToDate(to_date);
        setIngredient(ingredient);
        setOrderStatus(order_status);
        setOrderId(order_id);
        // Submit fetches with the values it was handed rather than leaving it to a state change to
        // notice them. Pressing Submit without touching the filter sets the state to what it
        // already held, and a list rebuilt on that alone would never reload.
        getDairyIngredientOrderList({ from_date, to_date, ingredient, order_status, order_id });
    };

    const getMethodList = async () => {
        const response = await axios.get(
            `${baseUrl}/comman/list?table=method_master&condition=status=1`
        );
        setMethodList(response.data.data);
    };

    // Crates and Liters/Kgs are the same order counted two ways, so a row arrives with both filled
    // in: Required Qty is what the kitchen is short, and Required Qty / Qty per crate is that
    // amount in crates. A saved figure is never overwritten -- neither one already entered here nor
    // one that went out with a placed order.
    const withDefaultOrderQty = (row) => {
        // Available Stock and Required Qty are both worked out by subtracting one float from
        // another, so they reach the screen with a tail on them -- 4.1200000000000045 in a column
        // that is read as litres.
        const item = {
            ...row,
            available_qty: roundQty(row.available_qty),
            required_qty: roundQty(row.required_qty)
        };
        const stdQtyPerCrate = Number(item.std_qty_per_crate) || 0;
        if (item.order_status == 3 || Number(item.crates_qty) || !stdQtyPerCrate) return item;

        if (Number(item.order_qty)) {
            return { ...item, crates_qty: qtyToCrates(item.order_qty, stdQtyPerCrate) };
        }
        return {
            ...item,
            crates_qty: qtyToCrates(item.required_qty, stdQtyPerCrate),
            order_qty: Number(item.required_qty) || 0
        };
    };

    const getDairyIngredientOrderList = async (filters) => {
        const { from_date, to_date, ingredient: filterIngredient, order_status, order_id } = filters ?? {
            from_date: fromDate,
            to_date: toDate,
            ingredient: ingredient,
            order_status: orderStatus,
            order_id: orderId
        };

        setLoading(true)
        setNoRecordsFound(false)
        const response = await axios.post(`${baseUrl}/dairy_order/list`,
            {
                from_date: from_date,
                to_date: to_date,
                ingredient: filterIngredient?.value,
                order_status: order_status?.value,
                order_id: order_id?.value
            }
        );

        setIngredientOrderList(response.data.data.map(withDefaultOrderQty));
        setNoRecordsFound(response.data.data.length === 0);
        setLoading(false)
    }

    // Packs is the same order in theli: crates times the packets a crate holds, which is the
    // ingredient's other crate size. Read-only, and blank rather than 0 for an ingredient whose
    // Std. Qty. per Crate - Theli / Packets has not been entered yet.
    const packsFor = (item) => cratesToQty(item?.crates_qty, Number(item?.std_qty_per_crate_theli) || 0);

    const isSelected = (item) => selectedKeys.includes(rowKey(item));
    const selectedRows = ingredientOrderList.filter(isSelected);

    const displayOrderDetails = async (e, item) => {
        e.preventDefault();
        setLoading(true)
        const response = await axios.get(`${baseUrl}/dairy_order/order_details`, {
            params: {
                order_id: item.order_id 
            }
        });
        setOrderDetails(response.data.data);
        console.log("ordeer details:", orderDetails)
        setLoading(false)
        setShowOrderModal(true);
        setNoRecordsFound(response.data.data.length === 0);
    }

    const handleSubmit = async () => {
        if (selectedRows.length == 0) {
            alert("Please select at least one row");
            setSelectedKeys([]);
            return;
        }

        if (selectedRows.some(item => item.order_qty === "" || item.order_qty === null)) {
            alert("One or more items have an empty order qty value.");
            setSelectedKeys([]);
            return;
        }

        if (selectedRows.length > 0) {
            setOrderIngredientList(selectedRows);
            try {
                if (window.confirm(`Do you want to place the order for these ingredients?`)) {
                    const response = await axios.post(`${baseUrl}/dairy_order/place_order`,
                        { ingredientsList: selectedRows });
                    const msg = response.data.msg;
                    toast.success(msg);

                    getDairyIngredientOrderList();
                }
            } catch (error) {
                toast.error("Failed to add item. Please try again.");
            }
            setSelectedKeys([]);
        }
    };

    const handleSave = async () => {
        // if (selectedRows.length == 0) {
        //     alert("Please select at least one row");
        //     setSelectedKeys([]);
        //     return;
        // }

        // if (selectedRows.some(item => item.order_qty === "")) {
        //     alert("One or more items have an empty order qty value.");
        //     setSelectedKeys([]);
        //     return;
        // }

        // if (selectedRows.length > 0) {

            try {
                const response = await axios.post(`${baseUrl}/dairy_order/save`,
                    {
                        ingredientsList: ingredientOrderList
                    });
                toast.success("Data save successfully.");
                getDairyIngredientOrderList();
            } catch (error) {
                toast.error("Failed to add item. Please try again.");
            }
        // }
    }

    const handleInputChange = (index) => (e) => {
        const { name, value } = e.target;

        setIngredientOrderList((prevFormData) => {
            const updatedFormData = [...prevFormData];
            const currentItem = updatedFormData[index];
            const stdQtyPerCrate = Number(currentItem.std_qty_per_crate) || 0;

            const updates = {};

            if (name === 'available_qty') {
                // Update available stock
                updates.available_qty = value;
                // Recalculate required qty based on new available stock
                updates.required_qty = roundQty(Math.max(currentItem.total_required_qty - value, 0));
            }

            // Either box may be typed in, and the other follows. The edited box keeps the text as
            // typed -- recomputing it from its own conversion would fight the keystrokes.
            if (name === 'liters_kgs') {
                updates.liters_kgs = value;
                updates.order_qty = value;
                updates.crates_qty = qtyToCrates(value, stdQtyPerCrate);
            }

            if (name === 'crates_qty') {
                updates.crates_qty = value;
                updates.liters_kgs = cratesToQty(value, stdQtyPerCrate);
                updates.order_qty = updates.liters_kgs;
            }

            updatedFormData[index] = {
                ...currentItem,
                ...updates
            };

            return updatedFormData;
        });
    };

    const handleToggleSelect = (index) => {
        const key = rowKey(ingredientOrderList[index]);
        setSelectedKeys(selectedKeys.includes(key)
            ? selectedKeys.filter(selected => selected !== key) // Deselect
            : [...selectedKeys, key]); // Select
    };

    const handleSwitchSelectAll = () => {
        if (selectAll) {
            setSelectedKeys([]);
        } else {
            setSelectedKeys(ingredientOrderList.filter((item) => item.order_status == 1).map(rowKey)); // Select all rows
        }
        setSelectAll(!selectAll);
    };

    const displayIngredientDetail = async (e, item) => {
        e.preventDefault();
        setLoading(true)
        const condition = `ird.date = '${item.date}' AND ird.ingredient_id = '${item.ingredient_id}' AND irm.requirement_status = 2`;
        const RecipeDbData = await axios.get(`${baseUrl}/order/ingredient_list?condition=${condition}`);
        const ingredientsList = RecipeDbData.data.data;
        console.log("ingredients list:",ingredientsList)

        setItemIngredientList(ingredientsList);
        setLoading(false)
        setShowModal(true);
    }

    const clearItem = () => {
        setSelectedKeys([]);
        setItemIngredientList(null);
        setOrderIngredientList(null);
        getDairyIngredientOrderList();
    };

    return (
        <div className="p-4">
            {loading && <Loader />}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
                    Dairy Store Order
                </h1>
                <Toaster position="top-center" reverseOrder={false} />
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <DairyRequirementFilter handleFilterChange={handelFilterChange} page="order" />
            </div>
            {noRecordsFound && <NoRecordsFound />}

            {ingredientOrderList.length > 0 && (
                <>
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full mt-10 text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">
                                        <input
                                            type="checkbox"
                                            className="newrow checkboxAll"
                                            onChange={handleSwitchSelectAll}
                                            checked={selectAll}
                                        />
                                    </th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Ingredient</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Meal Date</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Gross Req. Qty</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Available Stock</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Required Qty</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" colSpan="4">Order Qty</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center" rowSpan="2">Order Status</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center min-w-[10rem] whitespace-nowrap" rowSpan="2">Order Id</th>
                                </tr>
                                <tr>
                                    <th scope="col" className="px-2 py-1 border border-black text-center">Liters/Kgs</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center">Crates</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center">Packs</th>
                                    <th scope="col" className="px-2 py-1 border border-black text-center">Qty/Crate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ingredientOrderList.map((item, index) => (
                                    <tr key={index} className="border border-black-b">
                                        {!(item.order_status == 3) && (
                                            <td className="px-2 py-1 border border-black text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected(item)}
                                                    onChange={() => handleToggleSelect(index)}
                                                />
                                            </td>
                                        )}
                                        {(item.order_status == 3) && (
                                            <td className="px-2 py-1 border border-black text-center"></td>
                                        )}
                                        <td className="px-2 py-1 border border-black text-center">{item.ingredient}</td>
                                        <td className="px-2 py-1 border border-black text-center">{getDateWithDay(item.date)}</td>
                                        <td className="px-2 py-1 border border-black text-center">
                                            <a
                                                href="#"
                                                className="underline text-blue-500"
                                                onClick={(e) => displayIngredientDetail(e, item)}
                                            >
                                                {item.gross_required_qty}
                                            </a>
                                        </td>
                                        <td className="px-2 py-1 border border-black text-center">
                                            <input
                                                type="number"
                                                name="available_qty"
                                                disabled={item.order_status == 3}
                                                value={item?.available_qty ?? 0}
                                                onChange={handleInputChange(index)}
                                                className={`px-2 py-1 border rounded-md w-20 ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                            />
                                        </td>
                                        <td className="px-2 py-1 border border-black text-center">{item.required_qty}</td>
                                        <td className="px-2 py-1 border border-black text-center">
                                            <input
                                                type="number"
                                                name="liters_kgs"
                                                disabled={item.order_status == 3}
                                                value={item?.order_qty ?? ''}
                                                onChange={handleInputChange(index)}
                                                className={`px-2 py-1 border rounded-md w-20 ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                            />
                                        </td>
                                        <td className="px-2 py-1 border border-black text-center">
                                            <input
                                                type="number"
                                                name="crates_qty"
                                                disabled={item.order_status == 3}
                                                value={item?.crates_qty ?? ''}
                                                onChange={handleInputChange(index)}
                                                className={`px-2 py-1 border rounded-md w-20 ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                            />
                                        </td>
                                        <td className="px-2 py-1 border border-black text-center bg-gray-100">{packsFor(item)}</td>
                                        <td className="px-2 py-1 border border-black text-center">{item.std_qty_per_crate}</td>
                                        <td className="px-2 py-1 border border-black text-center">
                                            {item.order_status == 1 ? "Pending Order" :
                                                (item.order_status == 2 ? "Pending Order" : "Order Placed")}
                                        </td>
                                        <td className="px-2 py-1 border border-black text-center text-blue-500 underline cursor-pointer min-w-[10rem] whitespace-nowrap">
                                            <a
                                                href="#"
                                                className="underline text-blue-500"
                                                onClick={(e) => displayOrderDetails(e, item)}
                                            >
                                                {item.order_id}
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden mt-6 space-y-4">
                        {/* Select All Card */}
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    className="newrow checkboxAll"
                                    onChange={handleSwitchSelectAll}
                                    checked={selectAll}
                                />
                                <span className="text-sm font-medium">Select All</span>
                            </label>
                        </div>

                        {/* Item Cards */}
                        {ingredientOrderList.map((item, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                {/* Header with checkbox and ingredient name */}
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                        {!(item.order_status == 3) && (
                                            <input
                                                type="checkbox"
                                                checked={isSelected(item)}
                                                onChange={() => handleToggleSelect(index)}
                                                className="mt-1"
                                            />
                                        )}
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{item.ingredient}</h3>
                                            <p className="text-sm text-gray-500">{getDateWithDay(item.date)}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${item.order_status == 3
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {item.order_status == 1 ? "Pending Order" :
                                                (item.order_status == 2 ? "Pending Order" : "Order Placed")}
                                        </span>
                                    </div>
                                </div>

                                {/* Quantities Grid */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-xs font-medium text-gray-600">Gross Required Qty</label>
                                            <div className="mt-1">
                                                <a
                                                    href="#"
                                                    className="text-blue-500 underline text-sm"
                                                    onClick={(e) => displayIngredientDetail(e, item)}
                                                >
                                                    {item.gross_required_qty}
                                                </a>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600">Required Qty</label>
                                            <div className="mt-1 text-sm font-medium">{item.required_qty}</div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-xs font-medium text-gray-600">Available Stock</label>
                                            <input
                                                type="number"
                                                name="available_qty"
                                                disabled={item.order_status == 3}
                                                value={item?.available_qty ?? 0}
                                                onChange={handleInputChange(index)}
                                                className={`mt-1 w-full px-2 py-1 border rounded-md text-sm ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600">Order Qty (Liters/Kgs)</label>
                                            <input
                                                type="number"
                                                name="liters_kgs"
                                                disabled={item.order_status == 3}
                                                value={item?.order_qty ?? ''}
                                                onChange={handleInputChange(index)}
                                                className={`mt-1 w-full px-2 py-1 border rounded-md text-sm ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Crates Info */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div>
                                        <label className="text-xs font-medium text-gray-600">Crates</label>
                                        <input
                                            type="number"
                                            name="crates_qty"
                                            disabled={item.order_status == 3}
                                            value={item?.crates_qty ?? ''}
                                            onChange={handleInputChange(index)}
                                            className={`mt-1 w-full px-2 py-1 border rounded-md text-sm ${item.order_status == 3 ? "" : "bg-orange-50"}`}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-600">Packs</label>
                                        <div className="mt-1 text-sm font-medium">{packsFor(item)}</div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-600">Qty per Crate</label>
                                        <div className="mt-1 text-sm font-medium">{item.std_qty_per_crate}</div>
                                    </div>
                                </div>

                                {/* Order ID */}
                                <div className="border-t pt-3">
                                    <label className="text-xs font-medium text-gray-600">Order ID</label>
                                    <div className="mt-1">
                                        <a
                                            href="#"
                                            className="text-blue-500 underline text-sm"
                                            onClick={(e) => displayOrderDetails(e, item)}
                                        >
                                            {item.order_id}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <IngredientBatchModel
                showModal={showModal}
                setShowModal={setShowModal}
                detailList={itemIngedientList}
                clearItem={clearItem}
                page="dairy_list"
                type={1}
            />
            <OrderIngredientModel
                showOrderModal={showOrderModal}
                setShowOrderModal={setShowOrderModal}
                ingredientList={orderDetails}
                clearItem={clearItem}
                page="dairy_order"
            />
            {ingredientOrderList.length > 0 && (
                <div className='m-5'>
                    <button
                        onClick={handleSave}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md mr-0 sm:mr-4 mb-2 sm:mb-0 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                    >
                        Save
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="w-full sm:w-auto px-4 py-2 bg-rose-500 text-white rounded-md hover:bg-rose-600 focus:outline-none focus:ring focus:ring-rose-200"
                    >
                        Place for Order
                    </button>
                </div>
            )}
        </div>
    );
}

export default DairyOrderList;

